import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { UserBandInfoForm } from "Bands/components";
import {
  clearActionMeta,
  dataMetaSelector,
  dispatchGetMyBandUsers,
  editBandUser,
} from "Bands/state";
import { BAND_ACTIONS, IBand, IBandUserProps } from "Bands/types";

import { useBandContext } from "Bands/contexts";
import { copy } from "shared/assets";
import { Band } from "shared/assets/icons";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IFormButtons, IStatus } from "shared/types";

const {
  profile: { bandDetails },
} = copy;

interface EditUserBandInfoModalProps {
  band: Partial<IBand>;
  handleClose: () => void;
}

export const EditUserBandInfoModal: FC<EditUserBandInfoModalProps> = ({ band, handleClose }) => {
  const dispatch = useDispatch();
  const { getBandUser } = useBandContext();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editBandUser));

  const currentBandUser = useMemo(
    () => (band.id ? getBandUser(band.id) || undefined : undefined),
    [band.id, getBandUser]
  );

  const handleSubmit = (bandUser: IBandUserProps) => {
    if (!band.id) return;
    if (!currentBandUser) return;

    dispatch(
      editBandUser({
        bandUserId: currentBandUser.id,
        bandUserData: bandUser,
      })
    );
  };

  const buttons: IFormButtons = {
    primary: {
      "data-testid": "edit_user_band_info_submit",
    },
    secondary: {
      onClick: handleClose,
    },
  };

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatchGetMyBandUsers(dispatch);

      dispatch(clearActionMeta(BAND_ACTIONS.editBandUser));

      handleClose();

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: bandDetails.messages.success,
        })
      );
    }
  }, [apiMeta, handleClose, dispatch]);

  return (
    <Modal
      title={`${bandDetails.editTitle} ${band.title}`}
      icon={Band}
      dataTestId="edit_band_info_modal"
    >
      <UserBandInfoForm
        defaultValues={currentBandUser}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        hasError={!!apiMeta?.error}
        buttons={buttons}
        apiError={apiMeta?.error}
      />
    </Modal>
  );
};
